/**
 * Pillar shell + shared sub-components used by all three pillars.
 *
 * Lives in its own file (rather than inside PillarCompliance.tsx) so the
 * sibling pillars don't import from a file claiming to own a different domain.
 */

import { Link } from 'react-router-dom'
import { ArrowRightIcon } from '@heroicons/react/24/outline'

import ScoreDeltaChip from './ScoreDeltaChip'
import LearnMoreLink from './LearnMoreLink'

export type PillarTone = 'compliance' | 'bpa' | 'identity'
// 'routine' mirrors PriorityAction.severity from posture-brief.ts so this
// component can render priority-action rows directly without a coercion step.
// It tones down to the same primary-green bar as 'low' — both are advisory.
export type ActionSeverity = 'crit' | 'high' | 'med' | 'low' | 'routine'

const TONE_STYLES: Record<PillarTone, {
  topRule: string
  iconBg: string
  ctaBorder: string
}> = {
  compliance: {
    topRule:   'rgb(var(--color-primary-500))',
    iconBg:    'rgba(118,214,157,0.2)',
    ctaBorder: 'rgb(var(--color-primary-500))',
  },
  bpa: {
    topRule:   'rgb(var(--color-secondary-blue-300))',
    iconBg:    'rgba(167,211,240,0.3)',
    ctaBorder: 'rgb(var(--color-secondary-blue-500))',
  },
  identity: {
    topRule:   'rgb(var(--color-secondary-sand-300))',
    iconBg:    'rgba(235,212,180,0.45)',
    ctaBorder: 'rgb(var(--color-secondary-sand-500))',
  },
}

const SEVERITY_BAR_COLOR: Record<ActionSeverity, string> = {
  crit:    '#E57373',
  high:    'rgb(var(--color-secondary-sand-500))',
  med:     'rgb(var(--color-secondary-blue-500))',
  low:     'rgb(var(--color-primary-500))',
  routine: 'rgb(var(--color-primary-500))',
}

export function PillarShell({
  tone,
  roman,
  eyebrow,
  Icon,
  ctaLabel,
  ctaHref,
  children,
}: {
  tone: PillarTone
  roman: string
  eyebrow: string
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  ctaLabel: string
  ctaHref: string
  children: React.ReactNode
}) {
  const t = TONE_STYLES[tone]
  return (
    <div className="relative flex flex-col p-7 transition-colors duration-200 hover:bg-primary-500/[0.02] lg:p-8">
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{ background: t.topRule }}
      />

      <header className="mb-6 flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {roman}
          </div>
          <h3 className="mt-1 text-[12px] font-bold uppercase tracking-[0.16em] text-slate-100 dark:text-slate-100">
            {eyebrow}
          </h3>
        </div>
        <div
          className="grid h-9 w-9 place-items-center rounded-[10px]"
          style={{ background: t.iconBg }}
          aria-hidden
        >
          <Icon className="h-4 w-4" style={{ color: '#212938' }} strokeWidth={2} />
        </div>
      </header>

      <div className="flex flex-1 flex-col">{children}</div>

      <footer className="mt-auto pt-6">
        <Link
          to={ctaHref}
          className="
            inline-flex items-center gap-2 border-b-2 pb-1
            text-[11px] font-bold uppercase tracking-[0.16em]
            text-slate-100 dark:text-slate-100
            transition-[gap] duration-200 hover:gap-4
          "
          style={{ borderColor: t.ctaBorder }}
        >
          {ctaLabel}
          <ArrowRightIcon className="h-3.5 w-3.5" />
        </Link>
      </footer>
    </div>
  )
}

const TREND_PILL_CLASS: Record<'up' | 'down' | 'flat', string> = {
  up:   'bg-primary-500/20 text-primary-700 dark:text-primary-400',
  down: 'bg-red-500/15 text-red-600 dark:text-red-400',
  flat: 'bg-secondaryBlue-300/30 text-secondaryBlue-700 dark:text-secondaryBlue-300',
}

const TREND_GLYPH = { up: '▲', down: '▼', flat: '—' } as const

export function HeroMetric({
  value,
  unit,
  isLoading,
  trend,
}: {
  value: number | null | undefined
  unit: string
  isLoading: boolean
  trend: { dir: 'up' | 'down' | 'flat'; label: string } | null
}) {
  const display = isLoading || value == null ? '—' : Math.round(value)

  return (
    <div className="flex items-baseline gap-3">
      <span
        className="font-display font-extrabold tracking-[-0.035em] text-slate-100 dark:text-slate-100"
        style={{ fontSize: 'clamp(40px, 4.2vw, 60px)', lineHeight: 1 }}
      >
        {display}
      </span>
      <span className="text-[22px] font-semibold text-slate-500">{unit}</span>
      {trend && (
        <span
          className={`ml-auto rounded-full px-2.5 py-1 text-[11px] font-bold ${TREND_PILL_CLASS[trend.dir]}`}
        >
          {TREND_GLYPH[trend.dir]} {trend.label}
        </span>
      )}
    </div>
  )
}

export function MiniGrid({
  cells,
}: {
  cells: { label: string; value: string; suffix?: string }[]
}) {
  return (
    <div className="mb-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-dark-700">
      {cells.map(c => (
        <div key={c.label} className="bg-dark-800 px-4 py-3">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            {c.label}
          </div>
          <div className="text-[18px] font-bold tracking-[-0.01em] text-slate-100 dark:text-slate-100">
            {c.value}
            {c.suffix && (
              <span className="ml-1 text-[11px] font-medium text-slate-500">{c.suffix}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export function ActionsBlock({
  label,
  allHref,
  ctaLabel = 'More items',
  children,
}: {
  label: string
  allHref: string
  /** CTA label for the link to the full list. Defaults to a generic advisor-tone phrase. */
  ctaLabel?: string
  children: React.ReactNode
}) {
  return (
    <div className="mt-1">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-100 dark:text-slate-100">
          {label}
        </span>
        <Link
          to={allHref}
          className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-700 hover:underline dark:text-primary-400"
        >
          {ctaLabel} →
        </Link>
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  )
}

export function Action({
  severity,
  title,
  meta,
  guardianScoreDelta,
  href,
  learnMoreUrl,
}: {
  severity: ActionSeverity
  title: string
  meta: string
  /** Optional Guardian Score lift estimate. Renders an inline chip; null/undefined hides it.
   *  Populated by priority-actions consumers (e.g. PillarBpa). Drift / identity rows
   *  pass null until ScoreDeltaCalculator gains per-pillar paths — see the TODO on
   *  ScoreDeltaCalculator.cs. */
  guardianScoreDelta?: number | null
  /** Click target. Priority-action rows pass the per-item `href` from
   *  `PriorityAction`; identity rows pass a page-level fallback. */
  href: string
  /** Optional Microsoft Learn (or other doc) URL — when present we render a
   *  small icon affordance the user can click to open the doc in a new tab.
   *  Doesn't replace the row's primary in-app navigation target. */
  learnMoreUrl?: string | null
}) {
  return (
    <Link
      to={href}
      className="
        group grid grid-cols-[auto_1fr_auto] items-center gap-3
        border-b border-dark-700 py-3 text-left
        transition-[padding] duration-150 last:border-b-0 hover:pl-1.5
      "
    >
      <span
        aria-hidden
        className="block h-8 w-1.5 rounded-[3px]"
        style={{ background: SEVERITY_BAR_COLOR[severity] }}
      />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-[13px] font-semibold leading-tight text-slate-100 dark:text-slate-100">
            {title}
          </div>
          <ScoreDeltaChip delta={guardianScoreDelta} />
          <LearnMoreLink url={learnMoreUrl} size="sm" />
        </div>
        <div className="mt-0.5 text-[11px] text-slate-500">{meta}</div>
      </div>
      <span className="text-[14px] text-slate-500 transition-transform group-hover:translate-x-1 group-hover:text-slate-100">
        ›
      </span>
    </Link>
  )
}
